<template>
  <div class="user-access-preview d-flex flex-column fill-height">
    <div v-if="loadingUser" class="d-flex align-center justify-center pa-4">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <template v-else>
      <v-expansion-panels multiple flat v-model="openPanels">

        <!-- Dashboards -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-capitalize font-weight-bold">
              Dashboards ({{ isAdmin && dashboardItems.length === 0 ? "All" : dashboardItems.length }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div
              v-if="isAdmin && dashboardItems.length === 0"
              class="text-caption text--secondary font-weight-medium py-2"
            >
              Full access
            </div>
            <div v-else-if="dashboardItems.length === 0" class="text-caption text--disabled py-2">
              None
            </div>
            <div v-else class="user-access-table-wrap">
              <v-data-table
                :headers="[{ text: 'Name', value: 'name', sortable: false }]"
                :items="dashboardItems"
                :items-per-page="-1"
                hide-default-header
                hide-default-footer
                dense
                :mobile-breakpoint="0"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Views -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-capitalize font-weight-bold">
              Views ({{ isAdmin && viewsTableItems.length === 0 ? "All" : viewsTableItems.length }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div
              v-if="isAdmin && viewsTableItems.length === 0"
              class="text-caption text--secondary font-weight-medium py-2"
            >
              Full access
            </div>
            <div v-else-if="viewsTableItems.length === 0" class="text-caption text--disabled py-2">
              None
            </div>
            <div v-else class="user-access-table-wrap">
              <v-data-table
                :headers="viewsTableHeaders"
                :items="viewsTableItems"
                :items-per-page="-1"
                hide-default-footer
                dense
                :mobile-breakpoint="0"
              >
                <template v-slot:item.create="{ item }">
                  <v-checkbox
                    :input-value="item.create"
                    color="success"
                    hide-details
                    dense
                    readonly
                    class="ma-0 pa-0"
                  />
                </template>
                <template v-slot:item.edit="{ item }">
                  <v-checkbox
                    :input-value="item.edit"
                    color="success"
                    hide-details
                    dense
                    readonly
                    class="ma-0 pa-0"
                  />
                </template>
              </v-data-table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Reports -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-capitalize font-weight-bold">
              Reports ({{ isAdmin && reportItems.length === 0 ? "All" : reportItems.length }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div
              v-if="isAdmin && reportItems.length === 0"
              class="text-caption text--secondary font-weight-medium py-2"
            >
              Full access
            </div>
            <div v-else-if="reportItems.length === 0" class="text-caption text--disabled py-2">
              None
            </div>
            <div v-else class="user-access-table-wrap">
              <v-data-table
                :headers="[{ text: 'Name', value: 'name', sortable: false }]"
                :items="reportItems"
                :items-per-page="-1"
                hide-default-header
                hide-default-footer
                dense
                :mobile-breakpoint="0"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

        <!-- Permissions -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <span class="text-capitalize font-weight-bold">
              Permissions ({{ totalPermCount }})
            </span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="permissionsTableItems.length === 0" class="text-caption text--disabled py-2">
              None
            </div>
            <div v-else class="user-access-table-wrap">
              <v-data-table
                :headers="permissionsTableHeaders"
                :items="permissionsTableItems"
                :items-per-page="-1"
                hide-default-header
                hide-default-footer
                dense
                :mobile-breakpoint="0"
              >
                <template v-slot:item.name="{ item }">
                  <span class="text-body-2 text-capitalize">{{ item.name }}</span>
                </template>
              </v-data-table>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>

      </v-expansion-panels>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { firestore, doc, getDoc } from "@/firebase/init";

const PERMISSION_LABELS = {
  allBrands:             "Brand - Show all active",
  cannotApplyDiscounts:  "Cannot apply discounts",
  allCountries:          "Brand - Show all countries",
  rawMaterials:          "Bypass Order / Add raw materials",
  specialAccountPricing: "Set special account pricing",
  editAccountStatus:     "Edit account status",
  lineItems:             "Edit sales order line items",
  payments:              "Add payments to sales orders",
  exportData:            "Export data",
  applyCredits:          "Apply credits to sales orders",
  importExternalOrders:  "Import external orders",
};

export default {
  name: "UserAccessPreview",

  data() {
    return {
      loadedUser:   null,
      loadingUser:  false,
      openPanels:   [0, 1, 2, 3],
    };
  },

  computed: {
    ...mapGetters([
      "getRightDrawer",
      "getPhaseViews",
      "getUserDashboards",
      "getReports",
      "getUserPermissions",
    ]),

    drawerData() {
      return this.getRightDrawer && this.getRightDrawer.data || null;
    },

    userId() {
      const item = this.drawerData && this.drawerData.item;
      return (item && (item.id || item.userId)) || null;
    },

    item() {
      if (this.loadedUser) {
        return {
          views:           Array.isArray(this.loadedUser.views)      ? this.loadedUser.views      : [],
          viewPermissions: this.loadedUser.viewPermissions            || {},
          dashboards:      Array.isArray(this.loadedUser.dashboards)  ? this.loadedUser.dashboards : [],
          reports:         Array.isArray(this.loadedUser.reports)     ? this.loadedUser.reports    : [],
        };
      }
      const passed = this.drawerData && this.drawerData.item;
      if (passed) {
        return {
          views:           Array.isArray(passed.views)      ? passed.views      : [],
          viewPermissions: passed.viewPermissions            || {},
          dashboards:      Array.isArray(passed.dashboards)  ? passed.dashboards : [],
          reports:         Array.isArray(passed.reports)     ? passed.reports    : [],
        };
      }
      return null;
    },

    permissionData() {
      const src = this.loadedUser || (this.drawerData && this.drawerData.item) || {};
      return {
        leaderboard:    !!src.leaderboard,
        filterLocation: !!src.filterLocation,
        permissions:    Array.isArray(src.permissions) ? src.permissions : [],
      };
    },

    totalPermCount() {
      const { leaderboard, filterLocation, permissions } = this.permissionData;
      return permissions.length + (leaderboard ? 1 : 0) + (filterLocation ? 1 : 0);
    },

    namedPermissionItems() {
      return this.permissionData.permissions.map((p) => ({
        name: this.resolvePermLabel(p),
      }));
    },

    permissionsTableHeaders() {
      return [{ text: "Permission", value: "name", sortable: false }];
    },

    permissionsTableItems() {
      const { leaderboard, filterLocation } = this.permissionData;
      const rows = [];
      if (leaderboard) rows.push({ name: "Show on leaderboard" });
      if (filterLocation) rows.push({ name: "Assigned locations only" });
      const named = this.namedPermissionItems.map((p) => ({ name: p.name }));
      return rows.concat(named);
    },

    isAdmin() {
      const roleMatch = (r) => (r || "").toString().toLowerCase() === "admin";
      const typeMatch = (at) => Number(at) >= 4;
      if (this.loadedUser) {
        return typeMatch(this.loadedUser.accountType) && roleMatch(this.loadedUser.role);
      }
      const passed = this.drawerData && this.drawerData.item;
      if (!passed) return false;
      const roleOk = roleMatch(passed.roleValue) || roleMatch(passed.role);
      const typeOk = typeMatch(passed.accountTypeValue)
        || (passed.accountType || "").toString().toLowerCase() === "admin";
      return roleOk && typeOk;
    },

    viewOptions() {
      return (this.getPhaseViews || []).map((pv) => ({
        value: pv.value,
        text:  pv.text
          ? pv.text.split(" ").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")
          : pv.value,
      }));
    },

    dashboardOptions() {
      return (this.getUserDashboards || []).map((d) => ({
        value: d.value,
        text:  d.text || d.value,
      }));
    },

    reportOptions() {
      return (this.getReports || []).map((r) => ({
        value: r.to,
        text:  r.text
          ? r.text.split(" ").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ")
          : r.to,
      }));
    },

    dashboardItems() {
      if (!this.item || !Array.isArray(this.item.dashboards)) return [];
      const opts = this.dashboardOptions;
      return this.item.dashboards.map((v) => {
        const val    = typeof v === "string" ? v : (v && v.value) || v;
        const option = opts.find((o) => o && (o.value === val || o.value === String(val)));
        return { name: (option && option.text) || val || "—" };
      });
    },

    viewsTableHeaders() {
      return [
        { text: "View",   value: "name",   sortable: false },
        { text: "Create", value: "create", sortable: false, width: "80px" },
        { text: "Edit",   value: "edit",   sortable: false, width: "80px" },
      ];
    },

    viewsTableItems() {
      if (!this.item || !Array.isArray(this.item.views)) return [];
      const vp   = this.item.viewPermissions || {};
      const opts = this.viewOptions;
      return this.item.views.map((viewId) => {
        const perm   = vp[viewId] || {};
        const option = opts.find((o) => o && (o.value === viewId || o.value === String(viewId)));
        const name   = (option && option.text) || viewId || "—";
        return { name, create: !!perm.create, edit: !!perm.edit };
      });
    },

    reportItems() {
      if (!this.item || !Array.isArray(this.item.reports)) return [];
      const opts = this.reportOptions;
      return this.item.reports.map((v) => {
        const val    = typeof v === "string" ? v : (v && v.value) || v;
        const option = opts.find((o) => o && (o.value === val || o.value === String(val)));
        return { name: (option && option.text) || val || "—" };
      });
    },
  },

  watch: {
    userId: {
      immediate: true,
      handler(userId) {
        if (!userId || this.getRightDrawer && this.getRightDrawer.type !== "userAccess") {
          this.loadedUser = null;
          return;
        }
        this.fetchUser(userId);
      },
    },
    "getRightDrawer.type": {
      immediate: true,
      handler(type) {
        if (type === "userAccess") {
          this.openPanels = [0, 1, 2, 3];
        }
      },
    },
  },

  methods: {
    async fetchUser(userId) {
      this.loadingUser = true;
      this.loadedUser  = null;
      try {
        const snap = await getDoc(doc(firestore, "users", userId));
        if (snap.exists()) {
          const data = snap.data();
          this.loadedUser = {
            views:           Array.isArray(data.views)      ? data.views      : [],
            viewPermissions: data.viewPermissions            || {},
            dashboards:      Array.isArray(data.dashboards)  ? data.dashboards : [],
            reports:         Array.isArray(data.reports)     ? data.reports    : [],
            permissions:     Array.isArray(data.permissions) ? data.permissions : [],
            leaderboard:     !!data.leaderboard,
            filterLocation:  !!data.filterLocation,
            accountType:     data.accountType,
            role:            data.role != null ? data.role : null,
          };
        } else {
          this.loadedUser = this._emptyUser();
        }
      } catch (e) {
        console.error("UserAccessPreview: failed to load user", e);
        this.loadedUser = this._emptyUser();
      } finally {
        this.loadingUser = false;
      }
    },

    _emptyUser() {
      return {
        views: [], viewPermissions: {}, dashboards: [], reports: [],
        permissions: [], leaderboard: false, filterLocation: false,
        accountType: undefined, role: null,
      };
    },

    resolvePermLabel(value) {
      const fromStore = (this.getUserPermissions || []).find((p) => p.value === value);
      return (fromStore && fromStore.text) || PERMISSION_LABELS[value] || value;
    },
  },
};
</script>

<style scoped>
.user-access-preview {
  flex: 1 1 0;
  min-height: 0;
}

.user-access-table-wrap {
  max-height: 240px;
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.user-access-table-wrap::-webkit-scrollbar { width: 6px; }
.user-access-table-wrap::-webkit-scrollbar-thumb {
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.2);
}
</style>
